<template>
  <div class="lf-dashboard" v-loading="loading">
    <div class="dashboard-toolbar">
      <div>
        <p>Subscription, growth, active usage, and collection reading signals.</p>
      </div>
      <div class="toolbar-actions">
        <el-radio-group v-model="rangeDays" size="small" @change="fetchStats">
          <el-radio-button :label="30">30D</el-radio-button>
          <el-radio-button :label="60">60D</el-radio-button>
          <el-radio-button :label="90">90D</el-radio-button>
        </el-radio-group>
        <el-button size="small" icon="el-icon-refresh" @click="fetchStats">Refresh</el-button>
      </div>
    </div>

    <el-empty v-if="!loading && !stats" description="No dashboard data" />

    <template v-if="stats">
      <div class="metric-strip">
        <div v-for="metric in headlineMetrics" :key="metric.label" class="metric-tile">
          <div class="metric-label">{{ metric.label }}</div>
          <div class="metric-value">{{ formatNumber(metric.value) }}</div>
          <div class="metric-note">{{ metric.note }}</div>
        </div>
      </div>

      <div class="section-grid two-columns">
        <section class="dashboard-panel">
          <div class="panel-header">
            <div>
              <h3>Current Subscription Mix</h3>
              <p>{{ formatNumber(current.total_users) }} total accounts</p>
            </div>
          </div>
          <div class="mix-list">
            <div v-for="item in subscriptionMix" :key="item.label" class="mix-row">
              <div class="mix-copy">
                <span class="mix-dot" :style="{ backgroundColor: item.color }"></span>
                <span>{{ item.label }}</span>
              </div>
              <div class="mix-number">{{ formatNumber(item.value) }}</div>
              <div class="mix-track">
                <span :style="{ width: percent(item.value, current.total_users), backgroundColor: item.color }"></span>
              </div>
            </div>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-header">
            <div>
              <h3>Subscriber Age</h3>
              <p>{{ formatNumber(current.subscribed) }} currently subscribed users</p>
            </div>
          </div>
          <div class="age-stack" :aria-label="'Subscriber age total ' + current.subscribed">
            <span
              v-for="segment in subscriptionAgeSegments"
              :key="segment.key"
              :style="{ width: percent(segment.value, current.subscribed), backgroundColor: segment.color }"
            ></span>
          </div>
          <div class="age-grid">
            <div v-for="segment in subscriptionAgeSegments" :key="segment.key" class="age-item">
              <span class="mix-dot" :style="{ backgroundColor: segment.color }"></span>
              <div>
                <strong>{{ formatNumber(segment.value) }}</strong>
                <small>{{ segment.label }}</small>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section class="dashboard-panel">
        <div class="panel-header">
          <div>
            <h3>People Trends</h3>
            <p>Daily movement over the selected range</p>
          </div>
          <div class="panel-actions">
            <span class="updated-at">Updated {{ formatDateTime(stats.generated_at) }}</span>
            <el-button size="mini" icon="el-icon-full-screen" @click="openPeopleTrendDialog">View Chart</el-button>
          </div>
        </div>
        <div class="trend-grid">
          <div v-for="chart in trendCards" :key="chart.key" class="trend-card">
            <div class="trend-head">
              <div>
                <div class="trend-label">{{ chart.label }}</div>
                <div class="trend-total">{{ formatNumber(total(chart.values)) }}</div>
              </div>
              <span :style="{ color: chart.color }">{{ formatNumber(lastValue(chart.values)) }} latest</span>
            </div>
            <div class="echart trend-chart" :ref="'trendChart-' + chart.key"></div>
          </div>
        </div>
      </section>

      <section class="dashboard-panel">
        <div class="panel-header">
          <div>
            <h3>Interactions</h3>
            <p>Favorites, AI questions, and shares by day</p>
          </div>
          <div class="panel-actions">
            <el-button size="mini" icon="el-icon-full-screen" @click="openInteractionDialog">View Chart</el-button>
          </div>
        </div>
        <div class="interaction-summary">
          <div class="echart interaction-chart" ref="interactionChart"></div>
          <div class="interaction-metrics">
            <div v-for="item in interactionCards" :key="item.key" class="interaction-metric">
              <span class="mix-dot" :style="{ backgroundColor: item.color }"></span>
              <div>
                <strong>{{ formatNumber(total(item.values)) }}</strong>
                <small>{{ item.label }}</small>
              </div>
              <em :style="{ color: item.color }">{{ formatNumber(lastValue(item.values)) }} latest</em>
            </div>
          </div>
        </div>
      </section>

      <div class="section-grid two-columns">
        <section class="dashboard-panel">
          <div class="panel-header">
            <div>
              <h3>Active Users</h3>
              <p>Rolling distinct users from active sessions</p>
            </div>
          </div>
          <div class="active-explainer">
            <span><strong>DAU</strong> last 24 hours</span>
            <span><strong>WAU</strong> last 7 days</span>
            <span><strong>MAU</strong> last 30 days</span>
          </div>
          <div class="active-grid">
            <div v-for="item in activeMetrics" :key="item.label" class="active-item">
              <span>{{ item.label }}</span>
              <strong>{{ formatNumber(item.value) }}</strong>
              <small>{{ item.note }}</small>
            </div>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-header">
            <div>
              <h3>Collection Status</h3>
              <p>Distinct users by current collection state</p>
            </div>
          </div>
          <div class="collection-status">
            <div v-for="item in collectionStatusItems" :key="item.label" class="status-row">
              <div class="status-label">
                <span class="mix-dot" :style="{ backgroundColor: item.color }"></span>
                <span>{{ item.label }}</span>
              </div>
              <strong>{{ formatNumber(item.value) }}</strong>
              <div class="mix-track">
                <span :style="{ width: percent(item.value, collectionStatusMax), backgroundColor: item.color }"></span>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section class="dashboard-panel">
        <div class="panel-header">
          <div>
            <h3>Collection Reading</h3>
            <p>Cards read and distinct collection readers by day</p>
          </div>
          <div class="panel-actions">
            <el-button size="mini" icon="el-icon-full-screen" @click="openCollectionReadingDialog">View Chart</el-button>
          </div>
        </div>
        <div class="echart collection-chart" ref="collectionChart"></div>
        <div class="chart-legend">
          <span><i class="legend-swatch cards"></i>Cards read</span>
          <span><i class="legend-swatch readers"></i>Readers</span>
        </div>
      </section>
    </template>

    <el-dialog
      title="People Trends"
      :visible.sync="trendDialogVisible"
      width="960px"
      top="5vh"
      class="trend-detail-dialog"
      @opened="handleTrendDialogOpened"
    >
      <div class="trend-detail-toolbar">
        <el-date-picker
          v-model="trendDialogRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          range-separator="to"
          start-placeholder="Start date"
          end-placeholder="End date"
        />
        <el-button
          size="small"
          type="primary"
          :loading="trendDialogLoading"
          @click="fetchTrendDialogStats"
        >
          Apply
        </el-button>
      </div>
      <div v-loading="trendDialogLoading" class="trend-detail-body">
        <div class="echart trend-detail-chart" ref="peopleTrendDialogChart"></div>
        <el-table
          :data="trendDialogRows"
          size="small"
          border
          class="trend-detail-table"
          max-height="280"
        >
          <el-table-column prop="date" label="Date" width="130" fixed />
          <el-table-column prop="new_users" label="New Users" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.new_users) }}</template>
          </el-table-column>
          <el-table-column prop="new_trials" label="New Trials" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.new_trials) }}</template>
          </el-table-column>
          <el-table-column prop="new_subscriptions" label="New Subscriptions" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.new_subscriptions) }}</template>
          </el-table-column>
          <el-table-column prop="cancellations" label="Cancellations" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.cancellations) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      title="Collection Reading"
      :visible.sync="collectionDialogVisible"
      width="960px"
      top="5vh"
      class="trend-detail-dialog"
      @opened="handleCollectionDialogOpened"
    >
      <div class="trend-detail-toolbar">
        <el-date-picker
          v-model="collectionDialogRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          range-separator="to"
          start-placeholder="Start date"
          end-placeholder="End date"
        />
        <el-button
          size="small"
          type="primary"
          :loading="collectionDialogLoading"
          @click="fetchCollectionDialogStats"
        >
          Apply
        </el-button>
      </div>
      <div v-loading="collectionDialogLoading" class="trend-detail-body">
        <div class="echart trend-detail-chart" ref="collectionDialogChart"></div>
        <el-table
          :data="collectionDialogRows"
          size="small"
          border
          class="trend-detail-table"
          max-height="280"
        >
          <el-table-column prop="date" label="Date" width="130" fixed />
          <el-table-column prop="cards_read" label="Cards Read" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.cards_read) }}</template>
          </el-table-column>
          <el-table-column prop="readers" label="Readers" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.readers) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <el-dialog
      title="Interactions"
      :visible.sync="interactionDialogVisible"
      width="960px"
      top="5vh"
      class="trend-detail-dialog"
      @opened="handleInteractionDialogOpened"
    >
      <div class="trend-detail-toolbar">
        <el-date-picker
          v-model="interactionDialogRange"
          type="daterange"
          size="small"
          value-format="yyyy-MM-dd"
          range-separator="to"
          start-placeholder="Start date"
          end-placeholder="End date"
        />
        <el-button
          size="small"
          type="primary"
          :loading="interactionDialogLoading"
          @click="fetchInteractionDialogStats"
        >
          Apply
        </el-button>
      </div>
      <div v-loading="interactionDialogLoading" class="trend-detail-body">
        <div class="echart trend-detail-chart" ref="interactionDialogChart"></div>
        <el-table
          :data="interactionDialogRows"
          size="small"
          border
          class="trend-detail-table"
          max-height="280"
        >
          <el-table-column prop="date" label="Date" width="130" fixed />
          <el-table-column prop="favorites" label="Favorites" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.favorites) }}</template>
          </el-table-column>
          <el-table-column prop="ai_questions" label="AI Questions" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.ai_questions) }}</template>
          </el-table-column>
          <el-table-column prop="shares" label="Shares" align="right">
            <template slot-scope="scope">{{ formatNumber(scope.row.shares) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getLearningFirstDashboardStats } from '@/apis/lfAdmin'

export default {
  name: 'LFAdminDashboard',
  data() {
    return {
      loading: false,
      rangeDays: 30,
      stats: null,
      charts: {},
      resizeTimer: null,
      trendDialogVisible: false,
      trendDialogLoading: false,
      trendDialogRange: [],
      trendDialogStats: null,
      collectionDialogVisible: false,
      collectionDialogLoading: false,
      collectionDialogRange: [],
      collectionDialogStats: null,
      interactionDialogVisible: false,
      interactionDialogLoading: false,
      interactionDialogRange: [],
      interactionDialogStats: null
    }
  },
  computed: {
    current() {
      return this.stats && this.stats.current ? this.stats.current : {
        total_users: 0,
        trialing: 0,
        free: 0,
        subscribed: 0,
        subscription_age: {}
      }
    },
    activeUsers() {
      return this.stats && this.stats.active_users ? this.stats.active_users : { dau: 0, wau: 0, mau: 0 }
    },
    headlineMetrics() {
      return [
        { label: 'Trialing', value: this.current.trialing, note: 'active trial access' },
        { label: 'Free', value: this.current.free, note: 'without active access' },
        { label: 'Subscribed', value: this.current.subscribed, note: 'paid or converted access' },
        { label: 'DAU', value: this.activeUsers.dau, note: 'last 24 hours' },
        { label: 'WAU', value: this.activeUsers.wau, note: 'last 7 days' },
        { label: 'MAU', value: this.activeUsers.mau, note: 'last 30 days' }
      ]
    },
    subscriptionMix() {
      return [
        { label: 'Trialing', value: this.current.trialing, color: '#1f6feb' },
        { label: 'Subscribed', value: this.current.subscribed, color: '#0f9f6e' },
        { label: 'Free', value: this.current.free, color: '#8b95a5' }
      ]
    },
    subscriptionAgeSegments() {
      const age = this.current.subscription_age || {}
      return [
        { key: 'month_1', label: 'First month', value: Number(age.month_1 || 0), color: '#1f6feb' },
        { key: 'month_2', label: 'Second month', value: Number(age.month_2 || 0), color: '#0f9f6e' },
        { key: 'month_3', label: 'Third month', value: Number(age.month_3 || 0), color: '#f59f00' },
        { key: 'month_3_plus', label: 'Over 3 months', value: Number(age.month_3_plus || 0), color: '#7c3aed' }
      ]
    },
    trendCards() {
      return this.buildTrendCardsFromStats(this.stats)
    },
    trendDialogCards() {
      return this.buildTrendCardsFromStats(this.trendDialogStats)
    },
    interactionCards() {
      return this.buildInteractionCardsFromStats(this.stats)
    },
    interactionDialogCards() {
      return this.buildInteractionCardsFromStats(this.interactionDialogStats)
    },
    trendDialogRows() {
      const trends = this.trendDialogStats && this.trendDialogStats.trends ? this.trendDialogStats.trends : {}
      const labels = trends.labels || []
      return labels.map((date, index) => ({
        date,
        new_users: Number((trends.new_users || [])[index] || 0),
        new_trials: Number((trends.new_trials || [])[index] || 0),
        new_subscriptions: Number((trends.new_subscriptions || [])[index] || 0),
        cancellations: Number((trends.cancellations || [])[index] || 0)
      }))
    },
    activeMetrics() {
      return [
        { label: 'DAU', value: this.activeUsers.dau, note: 'Rolling 24h' },
        { label: 'WAU', value: this.activeUsers.wau, note: 'Rolling 7d' },
        { label: 'MAU', value: this.activeUsers.mau, note: 'Rolling 30d' }
      ]
    },
    collectionStatus() {
      return this.stats && this.stats.collections && this.stats.collections.status_users
        ? this.stats.collections.status_users
        : { active: 0, completed: 0, parked: 0 }
    },
    collectionStatusItems() {
      return [
        { label: 'Active', value: Number(this.collectionStatus.active || 0), color: '#1f6feb' },
        { label: 'Completed', value: Number(this.collectionStatus.completed || 0), color: '#0f9f6e' },
        { label: 'Parked', value: Number(this.collectionStatus.parked || 0), color: '#f59f00' }
      ]
    },
    collectionStatusMax() {
      return this.maxValue(this.collectionStatusItems.map(item => item.value))
    },
    collectionDaily() {
      return this.stats && this.stats.collections && this.stats.collections.daily
        ? this.stats.collections.daily
        : { labels: [], cards_read: [], readers: [] }
    },
    collectionDialogDaily() {
      return this.collectionDialogStats && this.collectionDialogStats.collections && this.collectionDialogStats.collections.daily
        ? this.collectionDialogStats.collections.daily
        : { labels: [], cards_read: [], readers: [] }
    },
    collectionDialogRows() {
      const daily = this.collectionDialogDaily
      const labels = daily.labels || []
      return labels.map((date, index) => ({
        date,
        cards_read: Number((daily.cards_read || [])[index] || 0),
        readers: Number((daily.readers || [])[index] || 0)
      }))
    },
    interactionDaily() {
      return this.stats && this.stats.interactions
        ? this.stats.interactions
        : { labels: [], favorites: [], ai_questions: [], shares: [] }
    },
    interactionDialogDaily() {
      return this.interactionDialogStats && this.interactionDialogStats.interactions
        ? this.interactionDialogStats.interactions
        : { labels: [], favorites: [], ai_questions: [], shares: [] }
    },
    interactionDialogRows() {
      const daily = this.interactionDialogDaily
      const labels = daily.labels || []
      return labels.map((date, index) => ({
        date,
        favorites: Number((daily.favorites || [])[index] || 0),
        ai_questions: Number((daily.ai_questions || [])[index] || 0),
        shares: Number((daily.shares || [])[index] || 0)
      }))
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleWindowResize)
    this.fetchStats()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleWindowResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
    this.disposeCharts()
  },
  methods: {
    async fetchStats() {
      this.loading = true
      try {
        const res = await getLearningFirstDashboardStats({ days: this.rangeDays })
        if (res && res.data && Number(res.data.code) === 0) {
          this.stats = res.data.data
          this.$nextTick(this.renderCharts)
        } else {
          const msg = res && res.data ? res.data.msg : 'Failed to load dashboard'
          this.$message.error(msg)
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load dashboard')
      } finally {
        this.loading = false
      }
    },
    buildTrendCardsFromStats(stats) {
      const trends = stats && stats.trends ? stats.trends : {}
      const labels = trends.labels || []
      const cards = [
        { key: 'new_users', label: 'New Users', values: trends.new_users || [], color: '#1f6feb' },
        { key: 'new_trials', label: 'New Trials', values: trends.new_trials || [], color: '#0f9f6e' },
        { key: 'new_subscriptions', label: 'New Subscriptions', values: trends.new_subscriptions || [], color: '#7c3aed' },
        { key: 'cancellations', label: 'Cancellations', values: trends.cancellations || [], color: '#f97316' }
      ]
      return cards.map(card => ({ ...card, labels }))
    },
    buildInteractionCardsFromStats(stats) {
      const interactions = stats && stats.interactions ? stats.interactions : {}
      const labels = interactions.labels || []
      const cards = [
        { key: 'favorites', label: 'Favorites', values: interactions.favorites || [], color: '#1f6feb' },
        { key: 'ai_questions', label: 'AI Questions', values: interactions.ai_questions || [], color: '#7c3aed' },
        { key: 'shares', label: 'Shares', values: interactions.shares || [], color: '#0f9f6e' }
      ]
      return cards.map(card => ({ ...card, labels }))
    },
    openPeopleTrendDialog() {
      const labels = this.stats && this.stats.trends && this.stats.trends.labels ? this.stats.trends.labels : []
      const start = this.stats && this.stats.date_from ? this.stats.date_from : (labels.length ? labels[0] : '')
      const end = this.stats && this.stats.date_to ? this.stats.date_to : (labels.length ? labels[labels.length - 1] : '')
      this.trendDialogRange = start && end ? [start, end] : []
      this.trendDialogStats = this.stats
      this.trendDialogVisible = true
      this.$nextTick(() => {
        this.renderPeopleTrendDialogChart()
        this.resizeCharts()
      })
    },
    openCollectionReadingDialog() {
      const labels = this.collectionDaily.labels || []
      const start = this.stats && this.stats.date_from ? this.stats.date_from : (labels.length ? labels[0] : '')
      const end = this.stats && this.stats.date_to ? this.stats.date_to : (labels.length ? labels[labels.length - 1] : '')
      this.collectionDialogRange = start && end ? [start, end] : []
      this.collectionDialogStats = this.stats
      this.collectionDialogVisible = true
      this.$nextTick(() => {
        this.renderCollectionDialogChart()
        this.resizeCharts()
      })
    },
    openInteractionDialog() {
      const labels = this.interactionDaily.labels || []
      const start = this.stats && this.stats.date_from ? this.stats.date_from : (labels.length ? labels[0] : '')
      const end = this.stats && this.stats.date_to ? this.stats.date_to : (labels.length ? labels[labels.length - 1] : '')
      this.interactionDialogRange = start && end ? [start, end] : []
      this.interactionDialogStats = this.stats
      this.interactionDialogVisible = true
      this.$nextTick(() => {
        this.renderInteractionDialogChart()
        this.resizeCharts()
      })
    },
    async fetchTrendDialogStats() {
      if (!this.trendDialogRange || this.trendDialogRange.length !== 2) {
        this.$message.warning('Please select a date range')
        return
      }
      this.trendDialogLoading = true
      try {
        const res = await getLearningFirstDashboardStats({
          date_from: this.trendDialogRange[0],
          date_to: this.trendDialogRange[1]
        })
        if (res && res.data && Number(res.data.code) === 0) {
          this.trendDialogStats = res.data.data
          this.trendDialogRange = [res.data.data.date_from, res.data.data.date_to]
          this.$nextTick(() => {
            this.renderPeopleTrendDialogChart()
            this.resizeCharts()
          })
        } else {
          const msg = res && res.data ? res.data.msg : 'Failed to load trend details'
          this.$message.error(msg)
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load trend details')
      } finally {
        this.trendDialogLoading = false
      }
    },
    async fetchCollectionDialogStats() {
      if (!this.collectionDialogRange || this.collectionDialogRange.length !== 2) {
        this.$message.warning('Please select a date range')
        return
      }
      this.collectionDialogLoading = true
      try {
        const res = await getLearningFirstDashboardStats({
          date_from: this.collectionDialogRange[0],
          date_to: this.collectionDialogRange[1]
        })
        if (res && res.data && Number(res.data.code) === 0) {
          this.collectionDialogStats = res.data.data
          this.collectionDialogRange = [res.data.data.date_from, res.data.data.date_to]
          this.$nextTick(() => {
            this.renderCollectionDialogChart()
            this.resizeCharts()
          })
        } else {
          const msg = res && res.data ? res.data.msg : 'Failed to load collection details'
          this.$message.error(msg)
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load collection details')
      } finally {
        this.collectionDialogLoading = false
      }
    },
    async fetchInteractionDialogStats() {
      if (!this.interactionDialogRange || this.interactionDialogRange.length !== 2) {
        this.$message.warning('Please select a date range')
        return
      }
      this.interactionDialogLoading = true
      try {
        const res = await getLearningFirstDashboardStats({
          date_from: this.interactionDialogRange[0],
          date_to: this.interactionDialogRange[1]
        })
        if (res && res.data && Number(res.data.code) === 0) {
          this.interactionDialogStats = res.data.data
          this.interactionDialogRange = [res.data.data.date_from, res.data.data.date_to]
          this.$nextTick(() => {
            this.renderInteractionDialogChart()
            this.resizeCharts()
          })
        } else {
          const msg = res && res.data ? res.data.msg : 'Failed to load interaction details'
          this.$message.error(msg)
        }
      } catch (error) {
        console.error(error)
        this.$message.error('Failed to load interaction details')
      } finally {
        this.interactionDialogLoading = false
      }
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    formatDateTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(Number(timestamp) * 1000)
      return date.toLocaleString()
    },
    percent(value, total) {
      const denominator = Number(total || 0)
      if (denominator <= 0) return '0%'
      return `${Math.max(0, Math.min(100, (Number(value || 0) / denominator) * 100)).toFixed(1)}%`
    },
    maxValue(values) {
      if (!values || !values.length) return 1
      return Math.max(1, ...values.map(value => Number(value || 0)))
    },
    total(values) {
      return (values || []).reduce((sum, value) => sum + Number(value || 0), 0)
    },
    lastValue(values) {
      if (!values || !values.length) return 0
      return Number(values[values.length - 1] || 0)
    },
    getChart(refName, chartKey) {
      const ref = this.$refs[refName]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (!el) return null
      if (!this.charts[chartKey]) {
        this.charts[chartKey] = echarts.init(el)
      }
      return this.charts[chartKey]
    },
    renderCharts() {
      if (!this.stats) return
      this.renderTrendCharts()
      this.renderInteractionChart()
      this.renderCollectionChart()
      if (this.trendDialogVisible) {
        this.renderPeopleTrendDialogChart()
      }
      if (this.collectionDialogVisible) {
        this.renderCollectionDialogChart()
      }
      if (this.interactionDialogVisible) {
        this.renderInteractionDialogChart()
      }
      this.resizeCharts()
    },
    renderTrendCharts() {
      this.trendCards.forEach((chart) => {
        const instance = this.getChart(`trendChart-${chart.key}`, chart.key)
        if (!instance) return
        instance.setOption(this.buildTrendChartOption(chart), true)
      })
    },
    renderCollectionChart() {
      const instance = this.getChart('collectionChart', 'collection_reading')
      if (!instance) return
      instance.setOption(this.buildCollectionChartOption(), true)
    },
    renderInteractionChart() {
      const instance = this.getChart('interactionChart', 'interactions')
      if (!instance) return
      instance.setOption(this.buildInteractionChartOption(), true)
    },
    renderPeopleTrendDialogChart() {
      const instance = this.getChart('peopleTrendDialogChart', 'people_trends_detail')
      if (!instance || !this.trendDialogStats) return
      instance.setOption(this.buildPeopleTrendDialogOption(), true)
    },
    renderCollectionDialogChart() {
      const instance = this.getChart('collectionDialogChart', 'collection_reading_detail')
      if (!instance || !this.collectionDialogStats) return
      instance.setOption(this.buildCollectionDialogOption(), true)
    },
    renderInteractionDialogChart() {
      const instance = this.getChart('interactionDialogChart', 'interactions_detail')
      if (!instance || !this.interactionDialogStats) return
      instance.setOption(this.buildInteractionDialogOption(), true)
    },
    handleTrendDialogOpened() {
      this.renderPeopleTrendDialogChart()
      this.resizeCharts()
    },
    handleCollectionDialogOpened() {
      this.renderCollectionDialogChart()
      this.resizeCharts()
    },
    handleInteractionDialogOpened() {
      this.renderInteractionDialogChart()
      this.resizeCharts()
    },
    buildTrendChartOption(chart) {
      return {
        animationDuration: 220,
        grid: { top: 6, right: 0, bottom: 0, left: 0 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const item = params && params[0] ? params[0] : {}
            return `${item.axisValue || ''}<br>${chart.label}: ${this.formatNumber(item.value)}`
          }
        },
        xAxis: {
          type: 'category',
          data: chart.labels,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false }
        },
        yAxis: {
          type: 'value',
          show: false,
          minInterval: 1,
          splitLine: { show: false }
        },
        series: [
          {
            name: chart.label,
            type: 'bar',
            data: chart.values,
            barMaxWidth: 8,
            itemStyle: {
              color: chart.color,
              borderRadius: [3, 3, 0, 0]
            }
          }
        ]
      }
    },
    buildCollectionChartOption() {
      const labels = this.collectionDaily.labels || []
      const cards = this.collectionDaily.cards_read || []
      const readers = this.collectionDaily.readers || []
      return {
        animationDuration: 220,
        grid: { top: 6, right: 6, bottom: 0, left: 0 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const rows = params || []
            const title = rows.length ? rows[0].axisValue : ''
            const body = rows.map(row => `${row.marker}${row.seriesName}: ${this.formatNumber(row.value)}`).join('<br>')
            return `${title}<br>${body}`
          }
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false }
        },
        yAxis: {
          type: 'value',
          show: false,
          minInterval: 1,
          splitLine: { show: false }
        },
        series: [
          {
            name: 'Cards read',
            type: 'bar',
            data: cards,
            barMaxWidth: 8,
            itemStyle: { color: '#1f6feb', borderRadius: [3, 3, 0, 0] }
          },
          {
            name: 'Readers',
            type: 'bar',
            data: readers,
            barMaxWidth: 8,
            itemStyle: { color: '#0f9f6e', borderRadius: [3, 3, 0, 0] }
          }
        ]
      }
    },
    buildInteractionChartOption() {
      const labels = this.interactionDaily.labels || []
      const cards = this.interactionCards
      return {
        animationDuration: 220,
        color: cards.map(item => item.color),
        grid: { top: 8, right: 8, bottom: 0, left: 0 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'line' },
          formatter: (params) => {
            const rows = params || []
            const title = rows.length ? rows[0].axisValue : ''
            const body = rows.map(row => `${row.marker}${row.seriesName}: ${this.formatNumber(row.value)}`).join('<br>')
            return `${title}<br>${body}`
          }
        },
        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { show: false }
        },
        yAxis: {
          type: 'value',
          show: false,
          minInterval: 1,
          splitLine: { show: false }
        },
        series: cards.map(card => ({
          name: card.label,
          type: 'line',
          data: card.values,
          showSymbol: false,
          smooth: true,
          lineStyle: { width: 2 },
          areaStyle: { opacity: 0.06 }
        }))
      }
    },
    buildPeopleTrendDialogOption() {
      const labels = this.trendDialogStats && this.trendDialogStats.trends
        ? this.trendDialogStats.trends.labels || []
        : []
      return {
        animationDuration: 240,
        color: this.trendDialogCards.map(chart => chart.color),
        legend: {
          top: 0,
          left: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: '#566176', fontSize: 12 }
        },
        grid: { top: 42, right: 18, bottom: 54, left: 52 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const rows = params || []
            const title = rows.length ? rows[0].axisValue : ''
            const body = rows.map(row => `${row.marker}${row.seriesName}: ${this.formatNumber(row.value)}`).join('<br>')
            return `${title}<br>${body}`
          }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 12, start: 0, end: 100 }
        ],
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#d8dee8' } },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 },
          splitLine: { lineStyle: { color: '#eef2f7' } }
        },
        series: this.trendDialogCards.map(chart => ({
          name: chart.label,
          type: 'bar',
          data: chart.values,
          barMaxWidth: 18,
          itemStyle: { borderRadius: [3, 3, 0, 0] }
        }))
      }
    },
    buildCollectionDialogOption() {
      const daily = this.collectionDialogDaily
      const labels = daily.labels || []
      return {
        animationDuration: 240,
        color: ['#1f6feb', '#0f9f6e'],
        legend: {
          top: 0,
          left: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: '#566176', fontSize: 12 }
        },
        grid: { top: 42, right: 18, bottom: 54, left: 58 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const rows = params || []
            const title = rows.length ? rows[0].axisValue : ''
            const body = rows.map(row => `${row.marker}${row.seriesName}: ${this.formatNumber(row.value)}`).join('<br>')
            return `${title}<br>${body}`
          }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 12, start: 0, end: 100 }
        ],
        xAxis: {
          type: 'category',
          data: labels,
          axisLine: { lineStyle: { color: '#d8dee8' } },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 },
          splitLine: { lineStyle: { color: '#eef2f7' } }
        },
        series: [
          {
            name: 'Cards read',
            type: 'bar',
            data: daily.cards_read || [],
            barMaxWidth: 18,
            itemStyle: { borderRadius: [3, 3, 0, 0] }
          },
          {
            name: 'Readers',
            type: 'bar',
            data: daily.readers || [],
            barMaxWidth: 18,
            itemStyle: { borderRadius: [3, 3, 0, 0] }
          }
        ]
      }
    },
    buildInteractionDialogOption() {
      const daily = this.interactionDialogDaily
      const labels = daily.labels || []
      const cards = this.interactionDialogCards
      return {
        animationDuration: 240,
        color: cards.map(item => item.color),
        legend: {
          top: 0,
          left: 0,
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { color: '#566176', fontSize: 12 }
        },
        grid: { top: 42, right: 18, bottom: 54, left: 58 },
        tooltip: {
          trigger: 'axis',
          confine: true,
          axisPointer: { type: 'line' },
          formatter: (params) => {
            const rows = params || []
            const title = rows.length ? rows[0].axisValue : ''
            const body = rows.map(row => `${row.marker}${row.seriesName}: ${this.formatNumber(row.value)}`).join('<br>')
            return `${title}<br>${body}`
          }
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 12, start: 0, end: 100 }
        ],
        xAxis: {
          type: 'category',
          data: labels,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#d8dee8' } },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#7b8494', fontSize: 11 },
          splitLine: { lineStyle: { color: '#eef2f7' } }
        },
        series: cards.map(card => ({
          name: card.label,
          type: 'line',
          data: card.values,
          showSymbol: false,
          smooth: true,
          lineStyle: { width: 2.2 }
        }))
      }
    },
    handleWindowResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null
        this.resizeCharts()
      }, 120)
    },
    resizeCharts() {
      Object.keys(this.charts).forEach((key) => {
        if (this.charts[key] && typeof this.charts[key].resize === 'function') {
          this.charts[key].resize()
        }
      })
    },
    disposeCharts() {
      Object.keys(this.charts).forEach((key) => {
        if (this.charts[key] && typeof this.charts[key].dispose === 'function') {
          this.charts[key].dispose()
        }
      })
      this.charts = {}
    }
  }
}
</script>

<style scoped lang="scss">
.lf-dashboard {
  color: #202631;
  padding: 18px 20px 28px;
  box-sizing: border-box;
}

.dashboard-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;

  p {
    margin: 0;
    color: #7b8494;
    font-size: 13px;
  }
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.metric-strip {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.metric-tile,
.dashboard-panel,
.trend-card {
  background: #ffffff;
  border: 1px solid #e3e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(24, 35, 56, 0.04);
}

.metric-tile {
  min-height: 96px;
  padding: 14px;
  box-sizing: border-box;
}

.metric-label {
  color: #758196;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.metric-value {
  margin-top: 10px;
  color: #182131;
  font-size: 28px;
  font-weight: 750;
  line-height: 1;
}

.metric-note {
  margin-top: 9px;
  color: #8993a4;
  font-size: 12px;
  line-height: 1.35;
}

.section-grid {
  display: grid;
  gap: 14px;
  margin-bottom: 14px;
}

.two-columns {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.dashboard-panel {
  padding: 18px;
  box-sizing: border-box;
  margin-bottom: 14px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    color: #252b35;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0;
  }

  p {
    margin: 5px 0 0;
    color: #818b9b;
    font-size: 12px;
  }
}

.updated-at {
  color: #8a94a6;
  font-size: 12px;
  white-space: nowrap;
}

.panel-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.mix-list,
.collection-status {
  display: grid;
  gap: 14px;
}

.mix-row,
.status-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) 68px minmax(160px, 1.4fr);
  gap: 12px;
  align-items: center;
}

.mix-copy,
.status-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #323a47;
  font-size: 13px;
  min-width: 0;
}

.mix-number,
.status-row strong {
  color: #1d2633;
  font-size: 13px;
  font-weight: 700;
  text-align: right;
}

.mix-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.mix-track {
  height: 8px;
  background: #eef2f7;
  border-radius: 999px;
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    min-width: 0;
    border-radius: inherit;
  }
}

.age-stack {
  display: flex;
  height: 14px;
  overflow: hidden;
  background: #eef2f7;
  border-radius: 999px;
  margin-bottom: 18px;

  span {
    min-width: 0;
  }
}

.age-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.age-item {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;

  strong {
    display: block;
    color: #1d2633;
    font-size: 18px;
    line-height: 1.1;
  }

  small {
    display: block;
    color: #7d8795;
    font-size: 12px;
    margin-top: 2px;
  }
}

.trend-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.trend-card {
  padding: 14px;
  box-sizing: border-box;
}

.trend-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;

  span {
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }
}

.trend-label {
  color: #768195;
  font-size: 12px;
  font-weight: 600;
}

.trend-total {
  margin-top: 4px;
  color: #1d2633;
  font-size: 24px;
  line-height: 1;
  font-weight: 750;
}

.echart {
  width: 100%;
  height: 128px;
  min-width: 0;
}

.active-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.active-explainer {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: -2px 0 12px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #667085;
    background: #f4f7fb;
    border: 1px solid #e5ebf3;
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 12px;
    line-height: 1;
  }

  strong {
    color: #273244;
    font-weight: 700;
  }
}

.active-item {
  min-height: 92px;
  padding: 14px;
  border: 1px solid #e7ebf2;
  border-radius: 8px;
  background: #f8fafc;
  box-sizing: border-box;

  span {
    color: #7b8494;
    font-size: 12px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 9px;
    color: #172033;
    font-size: 26px;
    line-height: 1;
  }

  small {
    display: block;
    margin-top: 9px;
    color: #8b95a5;
    font-size: 12px;
  }
}

.collection-chart {
  height: 180px;
}

.interaction-summary {
  display: grid;
  grid-template-columns: minmax(0, 1.8fr) minmax(260px, 0.8fr);
  gap: 18px;
  align-items: stretch;
}

.interaction-chart {
  height: 210px;
}

.interaction-metrics {
  display: grid;
  gap: 10px;
  align-content: center;
}

.interaction-metric {
  display: grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
  border: 1px solid #e7ebf2;
  border-radius: 8px;
  background: #f8fafc;
  box-sizing: border-box;

  strong {
    display: block;
    color: #182131;
    font-size: 18px;
    line-height: 1.1;
  }

  small {
    display: block;
    margin-top: 2px;
    color: #7b8494;
    font-size: 12px;
  }

  em {
    font-style: normal;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
  }
}

.trend-detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.trend-detail-body {
  min-height: 520px;
}

.trend-detail-chart {
  height: 320px;
  margin-bottom: 16px;
  border: 1px solid #e5ebf3;
  border-radius: 8px;
  padding: 12px 10px 4px;
  box-sizing: border-box;
  background: #fbfcfe;
}

.trend-detail-table {
  width: 100%;
}

.chart-legend {
  display: flex;
  gap: 18px;
  margin-top: 12px;
  color: #657083;
  font-size: 12px;
}

.legend-swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  margin-right: 6px;
  border-radius: 2px;

  &.cards {
    background: #1f6feb;
  }

  &.readers {
    background: #0f9f6e;
  }
}

@media (max-width: 1280px) {
  .metric-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .trend-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .interaction-summary {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .dashboard-toolbar,
  .toolbar-actions,
  .panel-header,
  .trend-detail-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .panel-actions {
    justify-content: flex-start;
  }

  .metric-strip,
  .two-columns,
  .trend-grid,
  .active-grid {
    grid-template-columns: 1fr;
  }

  .interaction-metric {
    grid-template-columns: 8px minmax(0, 1fr);

    em {
      grid-column: 2;
    }
  }

  .mix-row,
  .status-row {
    grid-template-columns: 1fr 64px;
  }

  .mix-track,
  .status-row .mix-track {
    grid-column: 1 / -1;
  }
}
</style>
